import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiChevronLeft,
  FiShare2,
  FiCheck,
  FiCopy,
  FiX,
  FiUserCheck,
  FiPaperclip,
  FiDownload,
  FiExternalLink,
  FiFileText,
  FiImage,
  FiMessageCircle,
} from 'react-icons/fi';
import { SiInstagram, SiFacebook } from 'react-icons/si';
import { FaXTwitter } from 'react-icons/fa6';
import { BadgeCheck, Ban, Clock3 } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import type { OfferDisplayStatus, OfferItem } from '../types';
import {
  getOfferDisplayStatus,
  resolveOfferPaymentStatus,
  withEffectiveOfferStatus,
} from '../types';
import { useOfferNow } from './useOfferNow';
import { getOfferPublishBlocker } from './offerPublishChecks';
import {
  findActivePaymentLink,
  findPendingSettlement,
  getOfferRemainingBalance,
  getPaymentRoles,
  MIN_OFFER_PAYMENT_AMOUNT,
  paymentLinkPayerTotal,
  rememberRecordedSettlement,
  type SessionPaymentLink,
} from './paymentLinks';
import { getOfferPaymentState, isOffPlatformPayment } from '../types';
import { formatMoney } from './offerFees';
import type { PaymentAmountOption } from './OfferModal';
import { RequestPaymentModal } from './RequestPaymentModal';
import {
  OffPlatformSettlementModal,
  type SettlementAmountPreset,
} from './OffPlatformSettlementModal';
import { OfferPaymentsList } from './OfferPaymentsList';
import { PendingSettlementCard } from './PendingSettlementCard';
import { toast } from '../../../../utils/toast';
import { useUserStore } from '../../../../store/useUserStore';
import { triggerFileDownload } from '../../../../utils/download';
import { formatFileSize } from '../../../../utils/uploadValidation';
import { formatDateTimeLong } from '../../../../utils/eventDate';
import {
  findConflictingActiveOffer,
  useAcceptOffer,
  useClaimOffer,
  useConfirmOffPlatformSettlement,
  useCreateOfferPaymentLink,
  useDeclineOffer,
  useOffersList,
  useProposeOffPlatformSettlement,
  useRejectOffPlatformSettlement,
  useRequestOfferChanges,
  useSubmitOffer,
  useWithdrawOffer,
} from '../../../../api/offers';
import {
  isStripeAccountReady,
  useStartStripeOnboarding,
  useStripeAccountStatus,
} from '../../../../api/stripe';
import { StripeOnboardingPromptModal } from './StripeOnboardingPromptModal';
import { OfferFeeSummary } from './OfferFeeSummary';
import { extractApiError } from '../../../../utils/apiError';
import { InfoTip, StatusTip, Tooltip } from '../../../ui/Tooltip';
import { HelpHint } from '../../../ui/HelpHint';
import { hints, offerStatusTips, paymentTips } from '../../../../onboarding/copy';
import { TOUR, tourTarget } from '../../../../onboarding/targets';
import { offerTourId } from '../../../../onboarding/tours/offers';
import { useTour } from '../../../../onboarding/useTour';
import { TakeTourButton } from '../../../../onboarding/TakeTourButton';

export type OfferDetailAction =
  | 'accept'
  | 'changes'
  | 'decline'
  | 'revise'
  | 'new'
  | 'stripe'
  | 'pay'
  | 'request'
  | 'offplatform'
  | 'settlement';

interface OfferDetailsViewProps {
  offer: OfferItem;
  onBack: () => void;
  onStatusChange?: (status: OfferItem['status']) => void;
  initialAction?: OfferDetailAction;
  /** Called once a deep-link action has been handled so the caller can drop it from the URL. */
  onActionHandled?: () => void;
}

function isImageAttachment(name: string, url: string): boolean {
  const haystack = `${name} ${url}`.toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(haystack);
}

function formatPaymentLinkDate(iso?: string | null): string {
  if (!iso) return 'Just now';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Just now';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isPdfAttachment(name: string, url: string): boolean {
  const haystack = `${name} ${url}`.toLowerCase();
  return /\.pdf(\?|$)/i.test(haystack) || haystack.includes('/raw/upload/');
}

function OfferAttachmentsSection({
  attachments,
}: {
  attachments: { name: string; url: string; file_size?: number }[];
}) {
  const [preview, setPreview] = useState<{ name: string; url: string } | null>(null);

  return (
    <>
      <div className="rounded-3xl border border-[#22242a] bg-[#121316] p-4 sm:p-6 space-y-4 shadow-sm">
        <h3 className="text-sm font-bold text-white">Attachments</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {attachments.map((att) => {
            const image = isImageAttachment(att.name, att.url);
            const pdf = !image && isPdfAttachment(att.name, att.url);
            const sizeLabel = att.file_size != null ? formatFileSize(att.file_size) : undefined;

            if (image) {
              return (
                <button
                  key={att.url}
                  type="button"
                  onClick={() => setPreview({ name: att.name, url: att.url })}
                  className="group relative overflow-hidden rounded-2xl border border-[#22242a] bg-[#0c0d0f] text-left cursor-pointer hover:border-zinc-600 transition"
                >
                  <div className="aspect-[4/3] bg-zinc-900/80">
                    <img
                      src={att.url}
                      alt={att.name}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-t border-[#22242a]">
                    <span className="inline-flex items-center gap-1.5 min-w-0 text-xs text-zinc-300">
                      <FiImage size={13} className="text-zinc-500 flex-shrink-0" />
                      <span className="truncate font-medium">{att.name}</span>
                    </span>
                    {sizeLabel && (
                      <span className="text-[10px] text-zinc-500 flex-shrink-0">{sizeLabel}</span>
                    )}
                  </div>
                  <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/20 transition" />
                </button>
              );
            }

            return (
              <div
                key={att.url}
                className="flex items-center gap-3 rounded-2xl border border-[#22242a] bg-[#0c0d0f] px-3.5 py-3"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-400 flex-shrink-0">
                  {pdf ? <FiFileText size={18} /> : <FiPaperclip size={18} />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-white truncate">{att.name}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    {sizeLabel || (pdf ? 'PDF' : 'File')}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Tooltip content="Open">
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 transition"
                      aria-label="Open"
                    >
                      <FiExternalLink size={14} />
                    </a>
                  </Tooltip>
                  <Tooltip content="Download">
                    <button
                      type="button"
                      onClick={() => triggerFileDownload(att.url, att.name)}
                      className="p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 transition cursor-pointer"
                      aria-label="Download"
                    >
                      <FiDownload size={14} />
                    </button>
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => setPreview(null)}
        >
          <div
            className="relative w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-sm font-semibold text-white truncate">{preview.name}</p>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => triggerFileDownload(preview.url, preview.name)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/10 transition cursor-pointer"
                >
                  <FiDownload size={13} /> Download
                </button>
                <button
                  type="button"
                  onClick={() => setPreview(null)}
                  className="p-2 rounded-full text-zinc-300 hover:text-white hover:bg-white/10 transition cursor-pointer"
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-black">
              <img
                src={preview.url}
                alt={preview.name}
                className="max-h-[80vh] w-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export const OfferDetailsView: React.FC<OfferDetailsViewProps> = ({
  offer: rawOffer,
  onBack,
  onStatusChange,
  initialAction,
  onActionHandled,
}) => {
  const now = useOfferNow(rawOffer);
  const offer = useMemo(() => withEffectiveOfferStatus(rawOffer, now), [rawOffer, now]);
  const navigate = useNavigate();
  const { user } = useUserStore();
  const currentUserId = user?.id != null ? String(user.id) : '';
  const currentUserEmail = (user?.email || '').toLowerCase();

  const paymentRoles = getPaymentRoles(offer, currentUserId, currentUserEmail);
  const { isCreator, isRecipient, isLinkGenerator, isPayer } = paymentRoles;

  const partyAvatar = isCreator ? offer.recipientAvatar : offer.creatorAvatar;

  const acceptOffer = useAcceptOffer();
  const declineOffer = useDeclineOffer();
  const requestChanges = useRequestOfferChanges();
  const createPaymentLink = useCreateOfferPaymentLink();
  const submitOffer = useSubmitOffer();
  const claimOffer = useClaimOffer();
  const withdrawOffer = useWithdrawOffer();
  const proposeSettlement = useProposeOffPlatformSettlement();
  const confirmSettlement = useConfirmOffPlatformSettlement();
  const rejectSettlement = useRejectOffPlatformSettlement();
  const startStripeOnboarding = useStartStripeOnboarding();
  const { data: stripeAccount, isLoading: stripeLoading } = useStripeAccountStatus();
  const { refetch: refetchMyOffers } = useOffersList(
    { perspective: 'created', page_size: 100 },
    { enabled: false },
  );
  const [showStripeSetupPrompt, setShowStripeSetupPrompt] = useState(false);
  const [showCreatorStripePrompt, setShowCreatorStripePrompt] = useState(false);
  const [checkingDraft, setCheckingDraft] = useState(false);
  const [stripeDeferred, setStripeDeferred] = useState(false);

  /** Stripe readiness of the signed-in user (candidate on gig offers, creator on direct offers). */
  const candidateStripeReady = isStripeAccountReady(stripeAccount);

  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [openPaymentOnAccept, setOpenPaymentOnAccept] = useState(true);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showRequestChangeModal, setShowRequestChangeModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const [payingKey, setPayingKey] = useState<string | null>(null);
  const [sessionPaymentLink, setSessionPaymentLink] = useState<SessionPaymentLink | null>(null);
  const [copiedPaymentLink, setCopiedPaymentLink] = useState(false);
  const [requestPaymentKey, setRequestPaymentKey] = useState<string | null>(null);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);

  const [declineChoice, setDeclineChoice] = useState<
    '' | 'Schedule Conflict' | 'Price Too Low' | 'Other'
  >('');
  const [declineOther, setDeclineOther] = useState('');

  const [whatToChange, setWhatToChange] = useState('');
  const [proposedPrice, setProposedPrice] = useState<number | ''>('');

  const isStandalone = !offer.gigUuid;

  const paymentProgress = {
    amountPaid: offer.amountPaid,
    remainingBalance: offer.remainingBalance,
    milestones: offer.milestones,
  };
  const effectivePaymentStatus = resolveOfferPaymentStatus(offer.paymentStatus, paymentProgress);
  const displayStatus: OfferDisplayStatus = getOfferDisplayStatus(
    offer.status,
    offer.paymentStatus,
    isRecipient,
    paymentProgress,
  );

  const deliverablesList = Array.isArray(offer.deliverables)
    ? offer.deliverables
    : typeof offer.deliverables === 'string'
      ? offer.deliverables.split('\n').filter(Boolean)
      : [];

  const gigOffer = Boolean(offer.gigUuid);

  const respondBlockedMessage = (): string => {
    if (offer.status === 'expired') {
      return 'This offer is no longer available.';
    }
    if (offer.status === 'accepted') return 'This offer has already been accepted.';
    if (offer.status === 'withdrawn') return 'This offer has already been withdrawn.';
    if (offer.status === 'rejected') return 'This offer has already been declined.';
    if (!isRecipient) return 'Only the offer recipient can respond to this offer.';
    return 'This offer is no longer awaiting a response.';
  };

  const sendNewOfferSearch = () => ({
    predecessorOfferUuid: offer.id,
    recipientEmail: offer.recipientEmail || undefined,
    recipientName: offer.recipientName || undefined,
    recipientUserId: offer.recipientUserId ?? undefined,
    conversationId: offer.conversationId || undefined,
    title: offer.title,
    description: offer.description,
    gigUuid: offer.gigUuid || undefined,
  });

  const claimAttempted = useRef(false);
  // Claim email-addressed offer when the logged-in user's email matches
  useEffect(() => {
    if (claimAttempted.current) return;
    if (
      !isCreator &&
      offer.recipientUserId == null &&
      offer.recipientEmail &&
      currentUserEmail &&
      offer.recipientEmail.toLowerCase() === currentUserEmail
    ) {
      claimAttempted.current = true;
      claimOffer.mutate();
    }
    // intentionally run once when offer loads for claimable state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.id, offer.recipientUserId, offer.recipientEmail, currentUserEmail, isCreator]);

  useEffect(() => {
    try {
      setStripeDeferred(sessionStorage.getItem(`musicade:stripe-deferred:${offer.id}`) === '1');
    } catch {
      setStripeDeferred(false);
    }
  }, [offer.id]);

  useEffect(() => {
    setAvatarFailed(false);
    setSessionPaymentLink(null);
    setCopiedPaymentLink(false);
  }, [partyAvatar, offer.id]);

  const generatePaymentLink = async (
    requestedAmount: number,
    milestoneIds: number[] = [],
  ): Promise<boolean> => {
    const key = milestoneIds.length > 0 ? String(milestoneIds[0]) : 'full';
    if (payingKey || createPaymentLink.isPending) return false;

    const remaining = getOfferRemainingBalance(offer);

    // Backend requires amount > 0 and <= remaining_balance
    const payable = Math.min(requestedAmount, remaining);
    const amount = Math.round(payable * 100) / 100;

    if (!(amount > 0)) {
      toast.error(
        remaining <= 0
          ? 'Nothing left to pay on this offer.'
          : 'Payment amount must be greater than zero.',
      );
      return false;
    }

    const settlesBalance = Math.abs(amount - Math.round(remaining * 100) / 100) < 0.005;
    if (amount < MIN_OFFER_PAYMENT_AMOUNT && !settlesBalance) {
      toast.error('Payments must be at least $10.00.');
      return false;
    }

    if (requestedAmount > remaining + 0.001) {
      toast.info(
        `Using remaining balance of ${offer.currency} ${amount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}.`,
      );
    }

    const validMilestoneIds = milestoneIds.filter((id) => Number.isFinite(id));

    setPayingKey(key);
    try {
      const result = await createPaymentLink.mutateAsync({
        offerUuid: offer.id,
        payload: { amount: amount.toFixed(2), milestone_ids: validMilestoneIds },
      });
      const url = result.stripe_payment_link_url;
      if (!url) {
        toast.error(
          "We couldn't process this payment. Please review the payment details and try again.",
        );
        return false;
      }
      setSessionPaymentLink({
        url,
        createdAt: new Date().toISOString(),
        amount,
        milestoneId: validMilestoneIds[0] ?? null,
      });
      setCopiedPaymentLink(false);
      toast.success(
        gigOffer
          ? `Payment requested. ${partyName} can now pay from the offer or chat.`
          : 'Payment link generated. Copy and send it to the person who will pay.',
      );
      return true;
    } catch {
      // toast in mutation
      return false;
    } finally {
      setPayingKey(null);
    }
  };

  const handleAcceptOffer = async () => {
    if (!isRecipient || offer.status !== 'pending') {
      setShowAcceptModal(false);
      toast.error(respondBlockedMessage());
      return;
    }
    try {
      const updated = await acceptOffer.mutateAsync(offer.id);
      setShowAcceptModal(false);
      onStatusChange?.(updated.status);
      if (gigOffer) {
        if (!candidateStripeReady) setShowStripeSetupPrompt(true);
      } else if (isStandalone) {
        const paymentUrl = updated.stripePaymentLinkUrl;
        if (paymentUrl) {
          setSessionPaymentLink({
            url: paymentUrl,
            createdAt: updated.acceptedAt || new Date().toISOString(),
            amount: updated.remainingBalance ?? updated.price,
            milestoneId: null,
          });
          if (openPaymentOnAccept) {
            window.open(paymentUrl, '_blank', 'noopener,noreferrer');
          }
        }
        requestAnimationFrame(() => {
          document.getElementById('offer-payment-section')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        });
      }
    } catch {
      // handled
    }
  };

  const handleDeclineOffer = async () => {
    if (!isRecipient || offer.status !== 'pending') {
      setShowDeclineModal(false);
      toast.error(respondBlockedMessage());
      return;
    }
    if (!declineChoice) {
      toast.error('Please select a reason for declining this offer.');
      return;
    }
    if (declineChoice === 'Other' && !declineOther.trim()) {
      toast.error('Please select a reason for declining this offer.');
      return;
    }
    const reason = declineChoice === 'Other' ? declineOther.trim() : declineChoice;
    try {
      const updated = await declineOffer.mutateAsync({ offerUuid: offer.id, reason });
      setShowDeclineModal(false);
      onStatusChange?.(updated.status);
    } catch {
      // handled
    }
  };

  const handleRequestChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isRecipient || offer.status !== 'pending') {
      setShowRequestChangeModal(false);
      toast.error(respondBlockedMessage());
      return;
    }
    if (!whatToChange.trim()) {
      toast.error('Please describe the changes you would like to request.');
      return;
    }
    try {
      const updated = await requestChanges.mutateAsync({
        offerUuid: offer.id,
        payload: {
          requested_changes: whatToChange.trim(),
          proposed_terms:
            typeof proposedPrice === 'number' && proposedPrice > 0
              ? String(proposedPrice)
              : undefined,
        },
      });
      setShowRequestChangeModal(false);
      setWhatToChange('');
      setProposedPrice('');
      onStatusChange?.(updated.status);
    } catch {
      // handled
    }
  };

  const handleWithdrawOffer = async () => {
    if (offer.status !== 'pending' && offer.status !== 'changes_requested') {
      setShowWithdrawModal(false);
      toast.error(
        offer.status === 'accepted'
          ? 'This offer has already been accepted and cannot be withdrawn.'
          : offer.status === 'withdrawn'
            ? 'This offer has already been withdrawn.'
            : 'This offer can no longer be withdrawn.',
      );
      return;
    }
    try {
      const updated = await withdrawOffer.mutateAsync(offer.id);
      setShowWithdrawModal(false);
      onStatusChange?.(updated.status);
    } catch {
      // handled
    }
  };

  const handleMilestoneLink = (milestoneId: string, amount: number) => {
    if (gigOffer) {
      if (!candidateStripeReady) {
        setShowStripeSetupPrompt(true);
        return;
      }
      setRequestPaymentKey(`m:${milestoneId}`);
      return;
    }
    if (needsStripeToRequest) {
      setShowCreatorStripePrompt(true);
      return;
    }
    void generatePaymentLink(amount, [Number(milestoneId)]);
  };

  const handleGenerateBalanceLink = () => {
    if (needsStripeToRequest) {
      setShowCreatorStripePrompt(true);
      return;
    }
    void generatePaymentLink(remainingBalance);
  };

  const handleOpenRequestPayment = () => {
    if (!candidateStripeReady) {
      toast.error('Complete payment setup to continue.');
      setShowStripeSetupPrompt(true);
      return;
    }
    setRequestPaymentKey('default');
  };

  const handleSubmitRequestPayment = async (option: PaymentAmountOption) => {
    const ok = await generatePaymentLink(option.amount, option.milestoneIds);
    if (ok) setRequestPaymentKey(null);
  };

  const handleRecordSettlement = async ({ amount, note }: { amount: number; note: string }) => {
    const knownIds = new Set((offer.payments ?? []).map((p) => p.id));
    try {
      const updated = await proposeSettlement.mutateAsync({
        offerUuid: offer.id,
        payload: { amount: amount.toFixed(2), note },
      });
      const recorded = (updated.payments ?? []).find(
        (p) =>
          isOffPlatformPayment(p) && getOfferPaymentState(p) === 'pending' && !knownIds.has(p.id),
      );
      if (recorded) rememberRecordedSettlement(offer.id, recorded.id);
      setShowSettlementModal(false);
    } catch {
      // toast in mutation
    }
  };

  const handleSendNewOffer = () => {
    navigate({ to: '/offers/create', search: sendNewOfferSearch() });
  };

  const goToEdit = () => navigate({ to: '/offers/$offerId/edit', params: { offerId: offer.id } });

  const handleSendDraft = async () => {
    if (offer.status !== 'draft') {
      if (offer.status === 'changes_requested') goToEdit();
      return;
    }
    if (checkingDraft || submitOffer.isPending) return;

    const blocker = getOfferPublishBlocker(offer);
    if (blocker) {
      toast.error(`${blocker} Update the offer before sending.`);
      goToEdit();
      return;
    }
    if (isStandalone && stripeLoading) {
      toast.error('Still checking your payment setup. Please try again in a moment.');
      return;
    }
    if (isStandalone && !candidateStripeReady) {
      setShowCreatorStripePrompt(true);
      return;
    }

    setCheckingDraft(true);
    try {
      const refreshed = await refetchMyOffers();
      if (refreshed.isError || !refreshed.data) {
        toast.error("We couldn't verify your existing offers. Please try again.");
        return;
      }
      const conflict = findConflictingActiveOffer(refreshed.data.results, {
        gigUuid: offer.gigUuid,
        recipientUserId: offer.recipientUserId,
        recipientEmail: offer.recipientUserId == null ? offer.recipientEmail : null,
        excludeOfferId: offer.id,
      });
      if (conflict) {
        toast.error(
          isStandalone
            ? 'This recipient already has an active direct offer.'
            : 'This candidate already has an active offer for this gig.',
        );
        return;
      }
      if (gigOffer && !offer.conversationId) {
        // Gig drafts saved before a chat existed must go through the form to open one.
        toast.info('Review and send this offer from the editor to share it in the candidate chat.');
        goToEdit();
        return;
      }
      const updated = await submitOffer.mutateAsync(offer.id);
      onStatusChange?.(updated.status);
    } catch {
      // handled
    } finally {
      setCheckingDraft(false);
    }
  };

  const stripeReturnUrl = () => {
    if (offer.conversationId) {
      const url = new URL('/messages', window.location.origin);
      url.searchParams.set('conversationUuid', offer.conversationId);
      url.searchParams.set('offerUuid', offer.id);
      return url.toString();
    }
    const url = new URL(`/offers/${offer.id}`, window.location.origin);
    url.searchParams.set('action', 'stripe');
    return url.toString();
  };

  const handleStartStripeSetup = async () => {
    if (stripeLoading) {
      toast.error('Unable to connect to Stripe. Please try again.');
      return;
    }
    try {
      const returnUrl = stripeReturnUrl();
      const result = await startStripeOnboarding.mutateAsync({
        returnUrl,
        refreshUrl: returnUrl,
      });
      const data = result as { onboarding_url?: string; dashboard_url?: string };
      const destination = data.onboarding_url || data.dashboard_url;
      if (!destination) {
        toast.error("We couldn't complete your payment setup. Please try again.");
        return;
      }
      window.location.assign(destination);
    } catch (err) {
      toast.error(
        extractApiError(err, "We couldn't complete your payment setup. Please try again."),
      );
    }
  };

  const handleDeferStripe = () => {
    setStripeDeferred(true);
    try {
      sessionStorage.setItem(`musicade:stripe-deferred:${offer.id}`, '1');
    } catch {
      // ignore storage failures
    }
    setShowStripeSetupPrompt(false);
    toast.info('Payment setup has been postponed. You can complete it later.');
  };

  const getOfferShareUrl = () => {
    return `${window.location.origin}/offers/${offer.id}/shared-link`;
  };

  const handleCopyShareLink = () => {
    navigator.clipboard?.writeText(getOfferShareUrl());
    toast.success('Offer link copied to clipboard!');
  };

  const handleSocialShare = (platform: 'instagram' | 'twitter' | 'facebook' | 'message') => {
    const shareUrl = getOfferShareUrl();
    const shareText = `Check out this offer: ${offer.title || 'Musicade Offer'}`;

    switch (platform) {
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
          '_blank',
          'noopener,noreferrer',
        );
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'noopener,noreferrer',
        );
        break;
      case 'message':
        window.open(`sms:?&body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`, '_self');
        break;
      case 'instagram':
        // Instagram has no web share intent — copy the link for the user to paste instead.
        navigator.clipboard?.writeText(shareUrl);
        toast.info('Link copied — paste it into your Instagram story or bio.');
        break;
    }
  };

  const getStatusBadge = (status: OfferDisplayStatus) => {
    switch (status) {
      case 'accepted':
        return isPayer && effectivePaymentStatus === 'unpaid' ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2a2010] px-3.5 py-1 text-xs font-semibold text-[#fbbf24] border border-[#4a3b1c]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#f59e0b]" /> Payment due
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0d2818] px-3.5 py-1 text-xs font-semibold text-[#4ade80] border border-[#1b432a]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" /> Offer accepted
          </span>
        );
      case 'awaiting_funding':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2a2010] px-3.5 py-1 text-xs font-semibold text-[#fbbf24] border border-[#4a3b1c]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#f59e0b]" /> Awaiting funding
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#182234] px-3.5 py-1 text-xs font-semibold text-[#60a5fa] border border-[#253956]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#3b82f6]" />{' '}
            {isRecipient ? 'Awaiting Review' : 'Offer sent'}
          </span>
        );
      case 'draft':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#182234] px-3.5 py-1 text-xs font-semibold text-[#60a5fa] border border-[#253956]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#3b82f6]" /> Draft
          </span>
        );
      case 'changes_requested':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0d2a33] px-3.5 py-1 text-xs font-semibold text-[#22d3ee] border border-[#164e63]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#06b6d4]" /> In negotiation
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#182234] px-3.5 py-1 text-xs font-semibold text-[#60a5fa] border border-[#253956]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#3b82f6]" /> In progress
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0d2818] px-3.5 py-1 text-xs font-semibold text-[#4ade80] border border-[#1b432a]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" /> Completed
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2d1416] px-3.5 py-1 text-xs font-semibold text-[#f87171] border border-[#4c1d24]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ef4444]" /> Declined
          </span>
        );
      case 'withdrawn':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800 px-3.5 py-1 text-xs font-semibold text-zinc-300 border border-zinc-700">
            <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" /> Withdrawn
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800 px-3.5 py-1 text-xs font-semibold text-zinc-300 border border-zinc-700">
            <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" /> Expired
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800 px-3 py-1 text-xs font-semibold text-zinc-300">
            {status}
          </span>
        );
    }
  };

  const partyName = isCreator
    ? offer.recipientName || offer.recipientEmail || 'Recipient'
    : offer.creatorName || 'Creator';

  const canRespond = isRecipient && offer.status === 'pending';
  const canEdit = isCreator && (offer.status === 'draft' || offer.status === 'changes_requested');
  const canWithdraw =
    isCreator && (offer.status === 'pending' || offer.status === 'changes_requested');
  const canRevise = isCreator && offer.status === 'changes_requested';
  const canSendNew =
    isCreator &&
    (offer.status === 'rejected' || offer.status === 'expired' || offer.status === 'withdrawn');
  const canResubmit = isCreator && offer.status === 'draft';
  const showExpiration =
    offer.status === 'draft' ||
    offer.status === 'pending' ||
    offer.status === 'changes_requested' ||
    offer.status === 'expired';
  const remainingBalance = getOfferRemainingBalance(offer);
  const offerFullyPaid = effectivePaymentStatus === 'paid' || remainingBalance <= 0.001;
  const hasOutstandingPayment =
    offer.status === 'accepted' &&
    !offerFullyPaid &&
    (effectivePaymentStatus === 'unpaid' ||
      effectivePaymentStatus === 'partially_paid' ||
      effectivePaymentStatus === 'failed');

  const isParty = isCreator || isRecipient;
  /**
   * The link generator receives the money (candidate on gig offers, creator on
   * direct offers), so their Stripe account must be ready before requesting payment.
   */
  const needsStripeToRequest = isLinkGenerator && !stripeLoading && !candidateStripeReady;
  const canGenerateLink = isLinkGenerator && hasOutstandingPayment;
  const canPayLink = isPayer && hasOutstandingPayment;

  const { autoStart } = useTour();
  const pageTourId = isParty ? offerTourId(gigOffer, isCreator) : null;
  const showPageTour = offer.status === 'accepted' || canRespond;
  useEffect(() => {
    if (pageTourId && showPageTour) autoStart(pageTourId);
  }, [pageTourId, showPageTour, autoStart]);

  const lastPaymentLink = useMemo(
    () => findActivePaymentLink(offer, sessionPaymentLink),
    [offer, sessionPaymentLink],
  );
  const activePaymentLink = lastPaymentLink && !lastPaymentLink.used ? lastPaymentLink : null;
  const activeLinkPayerTotal = activePaymentLink
    ? paymentLinkPayerTotal(activePaymentLink)
    : undefined;
  const payButtonLabel =
    activeLinkPayerTotal != null
      ? `Pay ${formatMoney(activeLinkPayerTotal, offer.currency)}`
      : 'Pay Now';
  const nextUnpaidMilestone = offer.milestones.find((m) => m.status !== 'paid');

  // TEMP-DISABLED (external payments hidden — chat only): restore settlement lookup to re-enable.
  // const pendingSettlement = isParty ? findPendingSettlement(offer, currentUserId) : null;
  // const canRecordSettlement = isParty && hasOutstandingPayment && !pendingSettlement;
  const pendingSettlement = null;
  const canRecordSettlement = false;
  const iAmPayee = isLinkGenerator;
  // TEMP: keep disabled settlement wiring referenced so lint passes; remove on re-enable.
  void [
    findPendingSettlement,
    PendingSettlementCard,
    OffPlatformSettlementModal,
    proposeSettlement,
    confirmSettlement,
    rejectSettlement,
    showSettlementModal,
    handleRecordSettlement,
    pendingSettlement,
    canRecordSettlement,
    iAmPayee,
  ];

  const actionHandled = useRef<string | null>(null);
  useEffect(() => {
    if (!initialAction) return;
    if (!currentUserId && !currentUserEmail) return;
    if (stripeLoading && ['stripe', 'request'].includes(initialAction)) return;
    const key = `${offer.id}:${initialAction}`;
    if (actionHandled.current === key) return;
    actionHandled.current = key;

    const paymentUnavailable = () =>
      offer.status !== 'accepted'
        ? 'Payments open once the offer has been accepted.'
        : offerFullyPaid
          ? 'This offer has been fully paid.'
          : 'There is no outstanding payment on this offer right now.';

    // Set when the action navigates off this page; clearing the `?action=` param
    // afterwards would replace that navigation and bounce back to the details page.
    let navigatedAway = false;
    switch (initialAction) {
      case 'accept':
      case 'changes':
      case 'decline': {
        if (!canRespond) {
          toast.error(respondBlockedMessage());
          break;
        }
        if (initialAction === 'accept') setShowAcceptModal(true);
        else if (initialAction === 'changes') setShowRequestChangeModal(true);
        else setShowDeclineModal(true);
        break;
      }
      case 'revise':
        if (canRevise) {
          goToEdit();
          navigatedAway = true;
        } else toast.info('This offer is not waiting on a revision.');
        break;
      case 'new':
        if (canSendNew) {
          handleSendNewOffer();
          navigatedAway = true;
        } else
          toast.info(
            'You can send a new offer once this one is declined, withdrawn or has expired.',
          );
        break;
      case 'stripe':
        if (!isLinkGenerator || offer.status !== 'accepted') {
          toast.info('Payment setup is not needed for this offer.');
        } else if (candidateStripeReady) {
          toast.info('Your payment setup is complete.');
        } else if (gigOffer) {
          setShowStripeSetupPrompt(true);
        } else {
          setShowCreatorStripePrompt(true);
        }
        break;
      case 'request':
        if (!canGenerateLink) {
          toast.info(paymentUnavailable());
        } else if (activePaymentLink) {
          toast.info('A payment request is already active for this offer.');
        } else if (needsStripeToRequest) {
          if (gigOffer) setShowStripeSetupPrompt(true);
          else setShowCreatorStripePrompt(true);
        } else if (gigOffer) {
          setRequestPaymentKey('default');
        } else {
          requestAnimationFrame(() =>
            document
              .getElementById('offer-payment-section')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
          );
        }
        break;
      // TEMP-DISABLED: record external payments hidden — chat only for now.
      // case 'offplatform':
      //   if (canRecordSettlement) setShowSettlementModal(true);
      //   else if (pendingSettlement)
      //     toast.info('A recorded payment is already waiting for confirmation.');
      //   else toast.info(paymentUnavailable());
      //   break;
      case 'offplatform':
        toast.info(paymentUnavailable());
        break;
      case 'pay':
      case 'settlement': {
        if (initialAction === 'pay' && !canPayLink) toast.info(paymentUnavailable());
        // TEMP-DISABLED: settlement confirm flow hidden.
        // if (initialAction === 'settlement' && !pendingSettlement)
        //   toast.info('There is no recorded payment waiting for confirmation.');
        requestAnimationFrame(() => {
          const target = document.getElementById('offer-payment-section');
          target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        break;
      }
    }
    if (!navigatedAway) onActionHandled?.();
    // Runs once per offer/action pair; the latest gates are read when it fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAction, offer.id, currentUserId, currentUserEmail, stripeLoading]);

  const requestPaymentOptions = useMemo<PaymentAmountOption[]>(() => {
    const options: PaymentAmountOption[] = [];
    const balance = Math.round(remainingBalance * 100) / 100;
    const unpaid = offer.milestones.filter((m) => m.status !== 'paid');
    const target =
      requestPaymentKey?.startsWith('m:') && unpaid.find((m) => `m:${m.id}` === requestPaymentKey);
    const milestone = target || unpaid[0];
    if (milestone) {
      options.push({
        key: `m:${milestone.id}`,
        label:
          target && target !== unpaid[0] ? milestone.title : `Next milestone: ${milestone.title}`,
        description: milestone.dueDate ? `Due ${milestone.dueDate}` : undefined,
        amount: Math.min(milestone.amount, balance),
        milestoneIds: [Number(milestone.id)],
      });
    }
    if (balance > 0 && (!milestone || Math.abs(milestone.amount - balance) > 0.005)) {
      options.push({
        key: 'full',
        label: 'Full remaining balance',
        description: unpaid.length > 1 ? `Covers ${unpaid.length} unpaid milestones` : undefined,
        amount: balance,
        milestoneIds: [],
      });
    }
    return options;
  }, [offer.milestones, remainingBalance, requestPaymentKey]);

  const settlementPresets = useMemo<SettlementAmountPreset[]>(() => {
    const balance = Math.round(remainingBalance * 100) / 100;
    const presets: SettlementAmountPreset[] = [];
    if (balance > 0) presets.push({ key: 'full', label: 'Remaining balance', amount: balance });
    if (nextUnpaidMilestone && Math.abs(nextUnpaidMilestone.amount - balance) > 0.005) {
      presets.push({
        key: `m:${nextUnpaidMilestone.id}`,
        label: nextUnpaidMilestone.title,
        amount: Math.min(nextUnpaidMilestone.amount, balance),
      });
    }
    return presets;
  }, [nextUnpaidMilestone, remainingBalance]);
  // TEMP: settlement presets unused while external payments hidden; remove on re-enable.
  void settlementPresets;

  const announcedLinkUrl = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!canPayLink) return;
    const url = activePaymentLink?.url ?? null;
    // First render only records the current link; later changes are announced.
    if (announcedLinkUrl.current === undefined) {
      announcedLinkUrl.current = url;
      return;
    }
    if (url && url !== announcedLinkUrl.current) {
      const milestoneTitle = activePaymentLink?.milestoneIds
        .map((id) => offer.milestones.find((m) => Number(m.id) === id)?.title)
        .filter(Boolean)
        .join(', ');
      const sender = gigOffer
        ? offer.recipientName || 'The recipient'
        : offer.creatorName || 'The creator';
      toast.success(
        `${sender} ${gigOffer ? 'requested payment' : 'sent a payment link'}${
          milestoneTitle ? ` for ${milestoneTitle}` : ''
        }.`,
      );
    }
    announcedLinkUrl.current = url;
  }, [
    canPayLink,
    activePaymentLink,
    gigOffer,
    offer.creatorName,
    offer.recipientName,
    offer.milestones,
  ]);

  const lastPaymentLinkMilestoneTitle =
    lastPaymentLink?.milestoneIds && lastPaymentLink.milestoneIds.length > 0
      ? lastPaymentLink.milestoneIds
          .map((id) => offer.milestones.find((m) => Number(m.id) === id)?.title)
          .filter((title): title is string => Boolean(title))
          .join(', ')
      : undefined;

  const handleCopyPaymentLink = async () => {
    if (!lastPaymentLink?.url) return;
    try {
      await navigator.clipboard.writeText(lastPaymentLink.url);
      setCopiedPaymentLink(true);
      toast.success(
        isPayer || gigOffer
          ? 'Payment link copied.'
          : 'Payment link copied. Send it to the person who will pay.',
      );
      window.setTimeout(() => setCopiedPaymentLink(false), 2000);
    } catch {
      toast.error('Could not copy the payment link. Please copy it manually.');
    }
  };

  const handlePayNow = () => {
    if (!lastPaymentLink?.url || lastPaymentLink.used) return;
    window.open(lastPaymentLink.url, '_blank', 'noopener,noreferrer');
  };

  const changeRequests = offer.changeRequests ?? [];
  const latestChangeRequest =
    changeRequests.length > 0 ? changeRequests[changeRequests.length - 1] : undefined;
  const olderChangeRequests =
    changeRequests.length > 1 ? [...changeRequests.slice(0, -1)].reverse() : [];

  const partyInitial =
    (partyName ? partyName.charAt(0).toUpperCase() : '') || offer.avatarInitial || '?';

  return (
    <div className="space-y-6 max-w-[1360px] mx-auto pb-24 text-white">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-zinc-400 mb-2">
            <button
              onClick={onBack}
              className="flex items-center gap-1 hover:text-white transition cursor-pointer"
            >
              <FiChevronLeft size={16} /> My Gigs
            </button>
            <span>/</span>
            <span className="text-zinc-300 font-normal">Details</span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
              {partyName}
            </h1>
            <StatusTip
              content={
                offerStatusTips[
                  displayStatus === 'accepted' && isPayer && effectivePaymentStatus === 'unpaid'
                    ? 'payment_due'
                    : displayStatus === 'pending' && isRecipient
                      ? 'awaiting_you'
                      : displayStatus
                ]
              }
              placement="bottom"
            >
              {getStatusBadge(displayStatus)}
            </StatusTip>
            <TakeTourButton tourId={pageTourId} />
          </div>
          {offer.status === 'expired' && (
            <p className="mt-2 text-sm font-medium text-amber-400">
              {isCreator
                ? 'This offer has expired. You can send a new offer to continue.'
                : 'This offer is no longer available.'}
            </p>
          )}
          {offer.status === 'withdrawn' && (
            <p className="mt-2 text-sm font-medium text-zinc-300">
              {isCreator
                ? 'You withdrew this offer. You can send a new offer to continue.'
                : 'This offer was withdrawn and can no longer be accepted.'}
            </p>
          )}
          {offer.status === 'rejected' && (
            <p className="mt-2 text-sm font-medium text-zinc-300">
              Offer declined. You can send a new offer to continue.
            </p>
          )}
          {offer.declineReason && (
            <p className="mt-1 text-sm text-zinc-400">Reason: {offer.declineReason}</p>
          )}
          <p className="text-xs text-zinc-500 mt-1">
            Created: {offer.createdAt || '—'}
            {offer.amountPaid != null && (
              <>
                {' '}
                · Paid: {offer.currency} {offer.amountPaid.toLocaleString()}
              </>
            )}
            {offer.remainingBalance != null && (
              <>
                {' '}
                · Remaining: {offer.currency} {offer.remainingBalance.toLocaleString()}
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3 self-start sm:self-auto flex-wrap">
          {offer.conversationId && (
            <button
              onClick={() =>
                navigate({
                  to: '/messages',
                  search: { conversationUuid: offer.conversationId!, offerUuid: offer.id },
                })
              }
              className="flex items-center gap-2 rounded-full bg-[#1784FF] px-4 py-2.5 text-xs font-bold text-white hover:bg-blue-600 transition cursor-pointer shadow-sm"
            >
              <FiMessageCircle size={15} /> View in Chat
            </button>
          )}
          <button
            onClick={() => setShowShareModal(true)}
            className="flex items-center gap-2 rounded-full border border-[#27292f] bg-[#141518] px-4 py-2.5 text-xs font-semibold text-zinc-300 hover:text-white hover:bg-[#1f2126] transition cursor-pointer shadow-sm"
          >
            <FiShare2 size={15} /> Share
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-3xl border border-[#22242a] bg-[#121316] p-4 sm:p-6 space-y-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                {partyAvatar && !avatarFailed ? (
                  <img
                    src={partyAvatar}
                    alt=""
                    onError={() => setAvatarFailed(true)}
                    className="h-12 w-12 rounded-xl object-cover border border-zinc-800 flex-shrink-0"
                  />
                ) : (
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-xl ${
                      offer.avatarColor || 'bg-amber-500'
                    } font-extrabold text-black text-xl flex-shrink-0`}
                  >
                    {partyInitial}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-white truncate">{partyName}</h3>
                    {offer.verified !== false && (
                      <span className="rounded-full bg-blue-500/15 px-2.5 py-0.5 text-[10px] font-semibold text-blue-400 border border-blue-500/25 flex items-center gap-1">
                        <FiUserCheck size={11} /> Verified
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    {isCreator
                      ? offer.recipientEmail || offer.recipientRole || 'Recipient'
                      : 'Offer creator'}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t border-[#22242a] pt-5 space-y-2">
              <h2 className="text-lg font-bold text-white">{offer.title}</h2>
              <p className="text-xs text-zinc-300 leading-relaxed font-normal">
                {offer.description}
              </p>
            </div>

            {(latestChangeRequest?.requestedChanges ||
              latestChangeRequest?.proposedTerms ||
              offer.feedback) && (
              <div className="rounded-2xl border border-[#164e63]/40 bg-[#0d2a33]/30 p-4 space-y-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-xs font-bold text-[#22d3ee] uppercase tracking-wider">
                      Change request
                    </h3>
                    {latestChangeRequest?.createdAt && (
                      <span className="text-[10px] text-zinc-500 shrink-0">
                        {formatPaymentLinkDate(latestChangeRequest.createdAt)}
                      </span>
                    )}
                  </div>
                  {(latestChangeRequest?.requestedChanges || offer.feedback) && (
                    <p className="text-xs text-zinc-300 leading-relaxed">
                      {latestChangeRequest?.requestedChanges || offer.feedback}
                    </p>
                  )}
                  {latestChangeRequest?.proposedTerms && (
                    <p className="text-xs text-zinc-400 leading-relaxed">
                      <span className="font-semibold text-zinc-300">Proposed terms: </span>
                      {latestChangeRequest.proposedTerms}
                    </p>
                  )}
                </div>

                {olderChangeRequests.length > 0 && (
                  <div className="space-y-2 border-t border-[#164e63]/25 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowChangeHistory((prev) => !prev)}
                      className="text-[11px] font-semibold text-[#22d3ee]/80 hover:text-[#22d3ee] transition cursor-pointer"
                    >
                      {showChangeHistory
                        ? 'Hide history'
                        : `View history (${olderChangeRequests.length})`}
                    </button>

                    {showChangeHistory && (
                      <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                        {olderChangeRequests.map((cr) => (
                          <div
                            key={cr.id}
                            className="rounded-xl border border-[#164e63]/20 bg-[#0a1f26]/40 px-3 py-2.5 space-y-1"
                          >
                            {cr.createdAt && (
                              <span className="block text-[10px] text-zinc-500">
                                {formatPaymentLinkDate(cr.createdAt)}
                              </span>
                            )}
                            {cr.requestedChanges && (
                              <p className="text-[11px] text-zinc-400 leading-relaxed">
                                {cr.requestedChanges}
                              </p>
                            )}
                            {cr.proposedTerms && (
                              <p className="text-[11px] text-zinc-500 leading-relaxed">
                                <span className="font-semibold text-zinc-400">
                                  Proposed terms:{' '}
                                </span>
                                {cr.proposedTerms}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-[#22242a] bg-[#121316] p-4 sm:p-6 space-y-4 shadow-sm">
            <h3 className="text-sm font-bold text-white">Timeline</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-[#22242a] bg-[#0c0d0f] p-4">
                <span className="block text-[10px] font-bold text-[#717684] uppercase tracking-wider mb-1.5">
                  START DATE
                </span>
                <span className="text-sm font-bold text-white">{offer.startDate || '—'}</span>
              </div>

              <div className="rounded-2xl border border-[#22242a] bg-[#0c0d0f] p-4">
                <span className="block text-[10px] font-bold text-[#717684] uppercase tracking-wider mb-1.5">
                  DELIVERY DATE
                </span>
                <span className="text-sm font-bold text-white">{offer.deliveryDate || '—'}</span>
              </div>

              {offer.expirationDate && showExpiration && (
                <div
                  className={`rounded-2xl border bg-[#0c0d0f] p-4 ${
                    offer.status === 'expired' ? 'border-amber-500/30' : 'border-[#22242a]'
                  }`}
                >
                  <span className="block text-[10px] font-bold text-[#717684] uppercase tracking-wider mb-1.5">
                    {offer.status === 'expired' ? 'EXPIRED' : 'EXPIRES'}
                  </span>
                  <span
                    className={`text-sm font-bold ${
                      offer.status === 'expired' ? 'text-amber-400' : 'text-white'
                    }`}
                  >
                    {formatDateTimeLong(offer.expirationDate, offer.timezone)}
                  </span>
                </div>
              )}
              {offer.cancellationPolicy && (
                <div className="rounded-2xl border border-[#22242a] bg-[#0c0d0f] p-4">
                  <span className="block text-[10px] font-bold text-[#717684] uppercase tracking-wider mb-1.5">
                    CANCELLATION
                  </span>
                  <span className="text-sm font-bold text-white capitalize">
                    {offer.cancellationPolicy}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-3xl border border-[#22242a] bg-[#121316] p-4 sm:p-6 space-y-4 shadow-sm">
            <h3 className="text-sm font-bold text-white">Deliverables</h3>
            <div className="space-y-3">
              {deliverablesList.length === 0 ? (
                <p className="text-xs text-zinc-500">No deliverables listed.</p>
              ) : (
                deliverablesList.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 flex-shrink-0">
                      <FiCheck size={12} />
                    </div>
                    <span className="text-xs text-zinc-300 font-medium">{item}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {offer.attachments && offer.attachments.length > 0 && (
            <OfferAttachmentsSection attachments={offer.attachments} />
          )}

          {isCreator && offer.internalNotes && (
            <div className="rounded-3xl border border-[#22242a] bg-[#121316] p-4 sm:p-6 space-y-3 shadow-sm">
              <h3 className="text-sm font-bold text-white">Internal Notes</h3>
              <p className="text-xs text-zinc-400 leading-relaxed whitespace-pre-wrap">
                {offer.internalNotes}
              </p>
              <p className="text-[10px] text-zinc-600">Only visible to you</p>
            </div>
          )}

          {offer.milestones && offer.milestones.length > 0 && (
            <div
              className="rounded-3xl border border-[#22242a] bg-[#121316] p-4 sm:p-6 space-y-4 shadow-sm"
              {...tourTarget(TOUR.offerSchedule)}
            >
              <h3 className="text-sm font-bold text-white">Payment Schedule</h3>
              <div className="space-y-3">
                {offer.milestones.map((m) => {
                  const isPaid = m.status === 'paid' || offerFullyPaid;
                  const rowLinkActive = !!activePaymentLink?.milestoneIds.includes(Number(m.id));
                  const showGenerate =
                    !isPaid &&
                    canGenerateLink &&
                    remainingBalance > 0 &&
                    !rowLinkActive &&
                    (!gigOffer || !activePaymentLink);
                  return (
                    <div
                      key={m.id}
                      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl border bg-[#0c0d0f] ${
                        isPaid ? 'border-[#1b432a]/80' : 'border-[#22242a]'
                      }`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div
                          className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0 ${
                            isPaid
                              ? 'bg-[#22c55e]/15 text-[#4ade80] ring-1 ring-[#22c55e]/25'
                              : 'bg-zinc-800/80 text-zinc-500 ring-1 ring-zinc-700/60'
                          }`}
                        >
                          {isPaid ? (
                            <BadgeCheck size={16} strokeWidth={2.25} />
                          ) : (
                            <Clock3 size={15} strokeWidth={2.25} />
                          )}
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-xs font-bold text-white truncate">{m.title}</h4>
                          <p className="text-[11px] text-[#717684] mt-0.5">
                            Due: {m.dueDate || '—'}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 self-stretch sm:self-auto pl-11 sm:pl-0 w-full sm:w-auto">
                        <span className="text-xs font-bold text-white tabular-nums">
                          {offer.currency}{' '}
                          {m.amount.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                        </span>

                        {isPaid ? (
                          <span className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0d2818] px-2.5 py-1 text-[11px] font-semibold text-[#4ade80] border border-[#1b432a]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" />
                            {m.status === 'paid' ? 'Paid' : 'Covered'}
                          </span>
                        ) : showGenerate ? (
                          <Tooltip
                            content={
                              gigOffer ? paymentTips.requestPayment : paymentTips.generateLink
                            }
                          >
                            <button
                              onClick={() =>
                                handleMilestoneLink(m.id, Math.min(m.amount, remainingBalance))
                              }
                              disabled={payingKey != null}
                              className="w-full sm:w-auto rounded-full bg-[#22c55e] px-4 py-1.5 text-xs font-bold text-black hover:bg-[#16a34a] transition cursor-pointer shadow-sm disabled:opacity-60"
                            >
                              {payingKey === m.id
                                ? 'Generating…'
                                : gigOffer
                                  ? 'Request Payment'
                                  : 'Generate Link'}
                            </button>
                          </Tooltip>
                        ) : rowLinkActive ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex flex-1 sm:flex-none items-center justify-center gap-1.5 rounded-full bg-[#2a2010] px-2.5 py-1 text-[11px] font-semibold text-[#fbbf24] border border-[#4a3b1c]">
                              <span className="h-1.5 w-1.5 rounded-full bg-[#f59e0b]" />
                              Payment requested
                            </span>
                            {canPayLink && (
                              <Tooltip content={paymentTips.pay}>
                                <button
                                  type="button"
                                  onClick={handlePayNow}
                                  className="rounded-full bg-[#22c55e] px-4 py-1.5 text-xs font-bold text-black hover:bg-[#16a34a] transition cursor-pointer shadow-sm"
                                >
                                  Pay
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center justify-center gap-1.5 rounded-full bg-zinc-800/80 px-2.5 py-1 text-[11px] font-semibold text-zinc-400 border border-zinc-700/70">
                            <span className="h-1.5 w-1.5 rounded-full bg-zinc-500" />
                            Unpaid
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div id="offer-payment-section" className="space-y-6">
          <div
            className="rounded-3xl border border-[#22242a] bg-[#121316] p-4 sm:p-6 space-y-5 shadow-sm"
            {...tourTarget(TOUR.offerSummary)}
          >
            <h3 className="text-base font-bold text-white">Offer Summary</h3>

            <div>
              <div className="text-3xl font-extrabold text-white tracking-tight">
                ${offer.price.toLocaleString('en-US', { minimumFractionDigits: 0 })}
              </div>
              <span className="text-xs text-[#717684]">Total offer value ({offer.currency})</span>
            </div>

            <OfferFeeSummary
              currency={offer.currency}
              gigAmount={offer.price}
              payments={offer.payments}
              isDirectOffer={isStandalone}
              perspective={isPayer ? 'payer' : isParty ? 'payee' : 'both'}
              viewerRole={isPayer ? 'payer' : isParty ? 'payee' : 'third_party'}
            />

            {canRespond && (
              <div className="space-y-2.5 pt-2" {...tourTarget(TOUR.offerResponse)}>
                <button
                  onClick={() => {
                    setOpenPaymentOnAccept(true);
                    setShowAcceptModal(true);
                  }}
                  className="w-full rounded-full bg-[#22c55e] py-3.5 px-6 text-sm font-bold text-black transition hover:bg-[#16a34a] cursor-pointer shadow-lg"
                >
                  Accept Offer
                </button>
                <button
                  onClick={() => setShowRequestChangeModal(true)}
                  className="w-full rounded-full border border-[#2e3138] bg-[#18191d] py-3 px-6 text-sm font-bold text-white transition hover:bg-[#24262c] cursor-pointer"
                >
                  Ask for Changes
                </button>
                <button
                  onClick={() => setShowDeclineModal(true)}
                  className="w-full rounded-full border border-[#2e3138] bg-[#18191d] py-3 px-6 text-sm font-bold text-[#ef4444] hover:bg-red-500/10 transition cursor-pointer"
                >
                  Decline
                </button>
              </div>
            )}

            {(canEdit || canSendNew || canWithdraw) && (
              <div className="space-y-2.5 pt-2">
                {canResubmit && (
                  <button
                    onClick={() => void handleSendDraft()}
                    disabled={submitOffer.isPending || checkingDraft}
                    className="w-full rounded-full bg-[#22c55e] py-3.5 px-6 text-sm font-bold text-black transition hover:bg-[#16a34a] cursor-pointer shadow-lg disabled:opacity-60"
                  >
                    {submitOffer.isPending || checkingDraft
                      ? 'Sending…'
                      : isStandalone
                        ? 'Send Offer'
                        : 'Send Offer to Chat'}
                  </button>
                )}
                {canRevise && (
                  <button
                    onClick={() =>
                      navigate({ to: '/offers/$offerId/edit', params: { offerId: offer.id } })
                    }
                    className="w-full rounded-full bg-[#1784FF] py-3.5 px-6 text-sm font-bold text-white transition hover:bg-blue-600 cursor-pointer shadow-lg"
                  >
                    Send Revised Offer
                  </button>
                )}
                {canSendNew && (
                  <button
                    onClick={handleSendNewOffer}
                    className="w-full rounded-full bg-[#1784FF] py-3.5 px-6 text-sm font-bold text-white transition hover:bg-blue-600 cursor-pointer shadow-lg"
                  >
                    Send New Offer
                  </button>
                )}
                {offer.status === 'draft' && (
                  <button
                    onClick={() =>
                      navigate({ to: '/offers/$offerId/edit', params: { offerId: offer.id } })
                    }
                    className="w-full rounded-full border border-[#2e3138] bg-[#18191d] py-3 px-6 text-sm font-bold text-white transition hover:bg-[#24262c] cursor-pointer"
                  >
                    Edit Offer
                  </button>
                )}
                {canWithdraw && (
                  <button
                    onClick={() => setShowWithdrawModal(true)}
                    className="w-full rounded-full border border-[#2e3138] bg-[#18191d] py-3 px-6 text-sm font-bold text-[#ef4444] hover:bg-red-500/10 transition cursor-pointer"
                  >
                    Withdraw Offer
                  </button>
                )}
              </div>
            )}

            {isParty && offer.status === 'accepted' && hasOutstandingPayment && (
              <HelpHint id={hints.offerPayments.id} title={hints.offerPayments.title}>
                {gigOffer
                  ? isLinkGenerator
                    ? hints.offerPayments.gigRecipient
                    : hints.offerPayments.gigCreator
                  : isLinkGenerator
                    ? hints.offerPayments.standaloneCreator
                    : hints.offerPayments.standaloneRecipient}
              </HelpHint>
            )}

            {canGenerateLink && gigOffer && needsStripeToRequest && (
              <div className="pt-2 space-y-3" {...tourTarget(TOUR.offerPaymentAction)}>
                <p className="text-sm text-zinc-400">
                  {stripeDeferred
                    ? 'Payment setup has been postponed. You can complete it later.'
                    : `Connect Stripe to request payment from ${partyName}.`}
                </p>
                <Tooltip content={paymentTips.stripeSetup}>
                  <button
                    type="button"
                    onClick={() => void handleStartStripeSetup()}
                    disabled={startStripeOnboarding.isPending}
                    className="w-full rounded-full bg-[#1784FF] py-3.5 px-6 text-sm font-bold text-white transition hover:bg-blue-600 cursor-pointer shadow-lg disabled:opacity-60"
                  >
                    {startStripeOnboarding.isPending ? 'Opening Stripe…' : 'Set Up Payment'}
                  </button>
                </Tooltip>
              </div>
            )}

            {canGenerateLink && gigOffer && !needsStripeToRequest && !activePaymentLink && (
              <div className="pt-2 space-y-2" {...tourTarget(TOUR.offerPaymentAction)}>
                <Tooltip content={paymentTips.requestPayment}>
                  <button
                    type="button"
                    onClick={handleOpenRequestPayment}
                    disabled={payingKey != null}
                    className="w-full rounded-full bg-[#22c55e] py-3.5 px-6 text-sm font-bold text-black transition hover:bg-[#16a34a] cursor-pointer shadow-lg disabled:opacity-60"
                  >
                    {payingKey != null ? 'Requesting…' : 'Request Payment'}
                  </button>
                </Tooltip>
                <p className="text-[11px] text-zinc-500 leading-relaxed">
                  {nextUnpaidMilestone
                    ? `Ask ${partyName} to pay the next milestone or the full balance through Stripe.`
                    : `Ask ${partyName} to pay the remaining balance through Stripe.`}
                </p>
              </div>
            )}

            {canGenerateLink && !gigOffer && !activePaymentLink && needsStripeToRequest && (
              <div className="pt-2 space-y-3">
                <p className="text-sm text-zinc-400">
                  Connect Stripe to generate payment links for this offer.
                </p>
                <button
                  type="button"
                  onClick={() => void handleStartStripeSetup()}
                  disabled={startStripeOnboarding.isPending}
                  className="w-full rounded-full bg-[#1784FF] py-3.5 px-6 text-sm font-bold text-white transition hover:bg-blue-600 cursor-pointer shadow-lg disabled:opacity-60"
                >
                  {startStripeOnboarding.isPending ? 'Opening Stripe…' : 'Set Up Payment'}
                </button>
              </div>
            )}

            {canGenerateLink && !gigOffer && !activePaymentLink && !needsStripeToRequest && (
              <div className="pt-2 space-y-2" {...tourTarget(TOUR.offerPaymentAction)}>
                {nextUnpaidMilestone ? (
                  <p className="text-[11px] text-zinc-500 leading-relaxed">
                    {`Use Generate Link on ${nextUnpaidMilestone.title} in the Payment Schedule, then send it to ${partyName}.`}
                  </p>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleGenerateBalanceLink}
                      disabled={payingKey != null}
                      className="w-full rounded-full bg-[#22c55e] py-3.5 px-6 text-sm font-bold text-black transition hover:bg-[#16a34a] cursor-pointer shadow-lg disabled:opacity-60"
                    >
                      {payingKey === 'full'
                        ? 'Generating…'
                        : `Generate Link · ${formatMoney(remainingBalance, offer.currency)}`}
                    </button>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">
                      {`Generate a payment link for the remaining balance, then send it to ${partyName}.`}
                    </p>
                  </>
                )}
              </div>
            )}

            {canGenerateLink && lastPaymentLink && (
              <div className="pt-2">
                <div className="rounded-2xl border border-[#22242a] bg-[#0c0d0f] p-3.5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">
                        {lastPaymentLink.used
                          ? gigOffer
                            ? 'Last paid request'
                            : 'Last used link'
                          : gigOffer
                            ? 'Payment requested'
                            : 'Last generated link'}
                      </p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">
                        {formatPaymentLinkDate(lastPaymentLink.createdAt)}
                        {lastPaymentLink.amount != null && Number.isFinite(lastPaymentLink.amount)
                          ? ` · ${offer.currency} ${lastPaymentLink.amount.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : ''}
                        {lastPaymentLinkMilestoneTitle ? ` · ${lastPaymentLinkMilestoneTitle}` : ''}
                      </p>
                    </div>
                    <span
                      className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                        lastPaymentLink.used
                          ? 'bg-[#0d2818] text-[#4ade80] border-[#1b432a]'
                          : 'bg-[#182234] text-[#60a5fa] border-[#253956]'
                      }`}
                    >
                      {lastPaymentLink.used ? 'Used' : 'Active'}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                    <div className="min-w-0 flex-1 rounded-xl border border-[#222222] bg-black px-3 py-2.5">
                      <p className="truncate text-xs text-zinc-300" title={lastPaymentLink.url}>
                        {lastPaymentLink.url}
                      </p>
                    </div>
                    {!lastPaymentLink.used && (
                      <button
                        type="button"
                        onClick={() => void handleCopyPaymentLink()}
                        className="inline-flex w-full sm:w-auto flex-shrink-0 items-center justify-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-xs font-bold text-black hover:bg-zinc-200 transition cursor-pointer"
                      >
                        {copiedPaymentLink ? <FiCheck size={14} /> : <FiCopy size={14} />}
                        {copiedPaymentLink ? 'Copied' : 'Copy'}
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-500 leading-relaxed">
                    {lastPaymentLink.used
                      ? gigOffer
                        ? 'This request was paid. Request payment again for the remaining balance.'
                        : 'This link already collected payment. Generate a new link for the remaining balance.'
                      : gigOffer
                        ? `Waiting for ${partyName} to pay. They can pay from this offer or the chat, and this page updates automatically.`
                        : 'Send this link to the person who will make the payment.'}
                  </p>
                </div>
              </div>
            )}

            {canPayLink && !activePaymentLink && (
              <div
                className="rounded-2xl border border-[#4a3b1c] bg-[#2a2010]/40 p-3.5"
                {...tourTarget(TOUR.offerPaymentAction)}
              >
                <p className="flex items-start gap-1.5 text-xs text-[#fbbf24] leading-relaxed">
                  <span>
                    {gigOffer
                      ? `Waiting for ${partyName} to request payment`
                      : `Waiting for ${partyName} to send the payment link`}
                    {nextUnpaidMilestone ? ` for ${nextUnpaidMilestone.title}` : ''}.
                  </span>
                  {gigOffer && (
                    <InfoTip
                      content={paymentTips.waitingForRequest}
                      label="Why am I waiting?"
                      size={13}
                      className="mt-0.5 text-[#fbbf24]/70 hover:text-[#fbbf24]"
                    />
                  )}
                </p>
                <p className="text-[11px] text-zinc-500 mt-1">
                  This page updates automatically when the link is ready.
                </p>
              </div>
            )}

            {canPayLink && activePaymentLink && lastPaymentLink && (
              <div className="pt-2 space-y-3" {...tourTarget(TOUR.offerPaymentAction)}>
                <Tooltip content={paymentTips.pay}>
                  <button
                    type="button"
                    onClick={handlePayNow}
                    className="w-full rounded-full bg-[#22c55e] py-3.5 px-6 text-sm font-bold text-black transition hover:bg-[#16a34a] cursor-pointer shadow-lg"
                  >
                    {payButtonLabel}
                  </button>
                </Tooltip>

                <div className="rounded-2xl border border-[#22242a] bg-[#0c0d0f] p-3.5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">
                        Payment link
                      </p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">
                        {formatPaymentLinkDate(lastPaymentLink.createdAt)}
                        {lastPaymentLink.amount != null && Number.isFinite(lastPaymentLink.amount)
                          ? ` · ${offer.currency} ${lastPaymentLink.amount.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : ''}
                        {lastPaymentLinkMilestoneTitle ? ` · ${lastPaymentLinkMilestoneTitle}` : ''}
                      </p>
                    </div>
                    <span className="inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border bg-[#182234] text-[#60a5fa] border-[#253956]">
                      Active
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                    <div className="min-w-0 flex-1 rounded-xl border border-[#222222] bg-black px-3 py-2.5">
                      <p className="truncate text-xs text-zinc-300" title={lastPaymentLink.url}>
                        {lastPaymentLink.url}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleCopyPaymentLink()}
                      className="inline-flex w-full sm:w-auto flex-shrink-0 items-center justify-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-xs font-bold text-black hover:bg-zinc-200 transition cursor-pointer"
                    >
                      {copiedPaymentLink ? <FiCheck size={14} /> : <FiCopy size={14} />}
                      {copiedPaymentLink ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-[11px] text-zinc-500 leading-relaxed">
                    Open the link to pay, or copy it to pay on another device.
                  </p>
                </div>
              </div>
            )}

            {/* TEMP-DISABLED: external payment confirm/reject hidden — chat only for now.
            {pendingSettlement && offer.status === 'accepted' && !offerFullyPaid && (
              <div {...tourTarget(TOUR.offerSettlement)}>
                <PendingSettlementCard
                  payment={pendingSettlement.payment}
                  currency={offer.currency}
                  counterpartyName={partyName}
                  waitingOnMe={pendingSettlement.waitingOnMe}
                  iAmPayee={iAmPayee}
                  isConfirming={confirmSettlement.isPending}
                  isRejecting={rejectSettlement.isPending}
                  onConfirm={() =>
                    confirmSettlement.mutateAsync({
                      offerUuid: offer.id,
                      paymentId: pendingSettlement.payment.id,
                      amount: pendingSettlement.payment.amount.toFixed(2),
                    })
                  }
                  onReject={() =>
                    rejectSettlement.mutateAsync({
                      offerUuid: offer.id,
                      paymentId: pendingSettlement.payment.id,
                    })
                  }
                />
              </div>
            )}

            {canRecordSettlement && (
              <div
                className="flex items-center justify-between gap-3 rounded-2xl border border-dashed border-[#2e3138] px-3.5 py-3"
                {...tourTarget(TOUR.offerOffPlatform)}
              >
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-white">
                    Paid outside Musicade?
                    <InfoTip
                      content={paymentTips.recordOffPlatform}
                      label="About outside payments"
                      size={12}
                    />
                  </p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">
                    Record cash, bank transfers and other payments.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSettlementModal(true)}
                  className="flex-shrink-0 rounded-full border border-[#2e3138] bg-[#18191d] px-4 py-2 text-xs font-bold text-white hover:bg-[#24262c] transition cursor-pointer"
                >
                  Record payment
                </button>
              </div>
            )}
            */}
          </div>

          {isParty && (
            <div {...tourTarget(TOUR.offerPaymentsList)}>
              <OfferPaymentsList
                payments={offer.payments ?? []}
                milestones={offer.milestones}
                currency={offer.currency}
              />
            </div>
          )}
        </div>
      </div>

      {showAcceptModal && canRespond && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto my-auto rounded-3xl border border-[#2c2d33] bg-[#141518] p-4 sm:p-6 shadow-2xl space-y-6 text-white">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold">Accept this offer?</h3>
              <button
                onClick={() => setShowAcceptModal(false)}
                className="text-zinc-400 hover:text-white transition cursor-pointer"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="flex flex-col items-center text-center space-y-3 py-2">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#22c55e]/15 text-[#4ade80] ring-1 ring-[#22c55e]/30 shadow-lg">
                <BadgeCheck size={32} strokeWidth={2} />
              </div>
              <p className="text-xs text-zinc-300 leading-relaxed max-w-xs">
                {isStandalone
                  ? "Are you sure you want to accept this offer? Once accepted, you'll proceed to payment."
                  : `Are you sure you want to accept this offer from ${offer.creatorName || 'the creator'}?`}
              </p>
            </div>

            {isStandalone && (
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider block">
                  After accepting
                </label>
                <div className="space-y-1.5">
                  <label
                    className={`flex items-center gap-3 p-3 rounded-xl border transition cursor-pointer text-xs ${
                      openPaymentOnAccept
                        ? 'border-[#22c55e]/50 bg-[#22c55e]/10 text-white'
                        : 'border-[#22242a] bg-[#0c0d0f] text-zinc-400 hover:text-zinc-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="acceptPaymentAction"
                      checked={openPaymentOnAccept}
                      onChange={() => setOpenPaymentOnAccept(true)}
                      className="accent-[#22c55e]"
                    />
                    <span>Open payment link now</span>
                  </label>
                  <label
                    className={`flex items-center gap-3 p-3 rounded-xl border transition cursor-pointer text-xs ${
                      !openPaymentOnAccept
                        ? 'border-[#22c55e]/50 bg-[#22c55e]/10 text-white'
                        : 'border-[#22242a] bg-[#0c0d0f] text-zinc-400 hover:text-zinc-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="acceptPaymentAction"
                      checked={!openPaymentOnAccept}
                      onChange={() => setOpenPaymentOnAccept(false)}
                      className="accent-[#22c55e]"
                    />
                    <span>I'll pay later from the offer page</span>
                  </label>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setShowAcceptModal(false)}
                className="flex-1 rounded-full border border-[#2e3138] bg-[#1a1b1f] py-3 text-xs font-bold text-white hover:bg-[#24262c] transition cursor-pointer"
              >
                Close
              </button>
              <button
                onClick={handleAcceptOffer}
                disabled={acceptOffer.isPending}
                className="flex-1 rounded-full bg-white py-3 text-xs font-bold text-black hover:bg-zinc-200 transition cursor-pointer shadow-md disabled:opacity-60"
              >
                {acceptOffer.isPending ? 'Accepting…' : 'Accept Offer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeclineModal && canRespond && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto my-auto rounded-3xl border border-[#2c2d33] bg-[#141518] p-4 sm:p-6 shadow-2xl space-y-5 text-white">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold">Decline this offer?</h3>
              <button
                onClick={() => setShowDeclineModal(false)}
                className="text-zinc-400 hover:text-white transition cursor-pointer"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="flex flex-col items-center text-center space-y-2 py-1">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#3d1a1e] text-[#f87171] border border-red-500/30">
                <Ban size={28} strokeWidth={2} />
              </div>
              <p className="text-xs text-zinc-300">
                Are you sure you want to decline this offer? This action cannot be undone.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider block">
                Reason for declining
              </label>
              <div className="space-y-1.5">
                {(['Schedule Conflict', 'Price Too Low', 'Other'] as const).map((opt) => (
                  <label
                    key={opt}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition cursor-pointer text-xs ${
                      declineChoice === opt
                        ? 'border-red-500/50 bg-red-500/10 text-white'
                        : 'border-[#22242a] bg-[#0c0d0f] text-zinc-400 hover:text-zinc-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="declineReason"
                      checked={declineChoice === opt}
                      onChange={() => setDeclineChoice(opt)}
                      className="accent-red-500"
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
              {declineChoice === 'Other' && (
                <textarea
                  rows={3}
                  value={declineOther}
                  onChange={(e) => setDeclineOther(e.target.value)}
                  placeholder="Tell the gig creator why you're declining"
                  className="w-full rounded-2xl border border-[#27292f] bg-[#0c0d0f] p-3 text-xs text-white placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none transition resize-none"
                />
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setShowDeclineModal(false)}
                className="flex-1 rounded-full border border-[#2e3138] bg-[#1a1b1f] py-3 text-xs font-bold text-white hover:bg-[#24262c] transition cursor-pointer"
              >
                Close
              </button>
              <button
                onClick={handleDeclineOffer}
                disabled={declineOffer.isPending}
                className="flex-1 rounded-full bg-white py-3 text-xs font-bold text-[#ef4444] hover:bg-zinc-200 transition cursor-pointer shadow-md disabled:opacity-60"
              >
                {declineOffer.isPending ? 'Declining…' : 'Decline Offer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRequestChangeModal && canRespond && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto my-auto rounded-3xl border border-[#2c2d33] bg-[#141518] p-4 sm:p-6 shadow-2xl space-y-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold">Ask for Changes</h3>
                <p className="text-xs text-zinc-400 mt-0.5">
                  Suggest changes to this offer before accepting.
                </p>
              </div>
              <button
                onClick={() => setShowRequestChangeModal(false)}
                className="text-zinc-400 hover:text-white transition cursor-pointer"
              >
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleRequestChangeSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-zinc-300 block mb-1.5">
                  What should change?
                </label>
                <textarea
                  rows={3}
                  value={whatToChange}
                  onChange={(e) => setWhatToChange(e.target.value)}
                  placeholder="Describe the changes you want"
                  className="w-full rounded-2xl border border-[#27292f] bg-[#0c0d0f] p-3 text-xs text-white placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none transition resize-none"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-zinc-300 block mb-1.5">
                  Proposed price (optional)
                </label>
                <input
                  type="number"
                  min={0}
                  value={proposedPrice}
                  onChange={(e) => setProposedPrice(e.target.value ? Number(e.target.value) : '')}
                  placeholder="0.00"
                  className="w-full rounded-2xl border border-[#27292f] bg-[#0c0d0f] p-3 text-xs text-white placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none transition"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={requestChanges.isPending}
                  className="w-full rounded-full bg-white py-3 text-xs font-bold text-black hover:bg-zinc-200 transition cursor-pointer shadow-md disabled:opacity-60"
                >
                  {requestChanges.isPending ? 'Submitting…' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showWithdrawModal && canWithdraw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto my-auto rounded-3xl border border-[#2c2d33] bg-[#141518] p-4 sm:p-6 shadow-2xl space-y-6 text-white">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold">Withdraw this offer?</h3>
              <button
                onClick={() => setShowWithdrawModal(false)}
                className="text-zinc-400 hover:text-white transition cursor-pointer"
              >
                <FiX size={20} />
              </button>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Are you sure you want to withdraw this offer? The recipient will no longer be able to
              accept it.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowWithdrawModal(false)}
                className="flex-1 rounded-full border border-[#2e3138] bg-[#18191d] py-3 text-xs font-bold text-white hover:bg-[#24262c] transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleWithdrawOffer}
                disabled={withdrawOffer.isPending}
                className="flex-1 rounded-full bg-[#ef4444] py-3 text-xs font-bold text-white hover:bg-[#dc2626] transition cursor-pointer disabled:opacity-60"
              >
                {withdrawOffer.isPending ? 'Withdrawing…' : 'Withdraw'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-[541px] my-auto max-h-[90vh] overflow-y-auto rounded-3xl border border-[#2c2d33] bg-black shadow-2xl text-white">
            <div className="flex items-start justify-between gap-3 border-b border-[#262626] p-4 sm:p-6">
              <div className="min-w-0">
                <h3 className="text-base font-bold">Share Offer</h3>
                <p className="text-xs text-zinc-400 mt-0.5">
                  Share this secure link to review and manage this offer.
                </p>
              </div>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-zinc-400 hover:text-white transition cursor-pointer flex-shrink-0"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-5">
              <div className="rounded-3xl border border-[#1f1f1f] bg-[#0d0d0d] p-4 space-y-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div className="h-11 w-full min-w-0 rounded-xl border border-[#222222] bg-black px-3.5 py-3">
                    <input
                      type="text"
                      readOnly
                      value={getOfferShareUrl()}
                      className="h-full w-full bg-transparent text-sm text-zinc-300 focus:outline-none truncate"
                    />
                  </div>
                  <button
                    onClick={handleCopyShareLink}
                    className="w-full sm:w-auto flex-shrink-0 rounded-full bg-white px-6 py-3 text-xs font-bold text-black hover:bg-zinc-200 transition cursor-pointer shadow-md"
                  >
                    Copy Link
                  </button>
                </div>
                <p className="text-center text-xs text-zinc-500">
                  Or share directly on your favorite platform
                </p>
              </div>

              <div className="flex items-center justify-center gap-4 pt-1">
                <Tooltip content="Share on Instagram">
                  <button
                    onClick={() => handleSocialShare('instagram')}
                    aria-label="Share on Instagram"
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1a1a1a] text-white hover:bg-[#242424] transition cursor-pointer"
                  >
                    <SiInstagram size={18} />
                  </button>
                </Tooltip>
                <Tooltip content="Share on X">
                  <button
                    onClick={() => handleSocialShare('twitter')}
                    aria-label="Share on X"
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1a1a1a] text-white hover:bg-[#242424] transition cursor-pointer"
                  >
                    <FaXTwitter size={18} />
                  </button>
                </Tooltip>
                <Tooltip content="Share on Facebook">
                  <button
                    onClick={() => handleSocialShare('facebook')}
                    aria-label="Share on Facebook"
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1a1a1a] text-white hover:bg-[#242424] transition cursor-pointer"
                  >
                    <SiFacebook size={18} />
                  </button>
                </Tooltip>
                <Tooltip content="Share via message">
                  <button
                    onClick={() => handleSocialShare('message')}
                    aria-label="Share via message"
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1a1a1a] text-white hover:bg-[#242424] transition cursor-pointer"
                  >
                    <FiMessageCircle size={18} />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      )}

      {showStripeSetupPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto my-auto rounded-3xl border border-[#2c2d33] bg-[#141518] p-4 sm:p-6 shadow-2xl space-y-5 text-white">
            <h3 className="text-base font-bold">Set up payment</h3>
            <p className="text-sm text-zinc-300 leading-relaxed">
              Connect Stripe so the gig creator can pay you for this gig. You can also do this
              later.
            </p>
            <button
              type="button"
              onClick={() => void handleStartStripeSetup()}
              disabled={startStripeOnboarding.isPending}
              className="w-full rounded-full bg-[#1784FF] py-3 text-sm font-bold text-white hover:bg-blue-600 transition cursor-pointer disabled:opacity-60"
            >
              {startStripeOnboarding.isPending ? 'Opening Stripe…' : 'Set Up Payment'}
            </button>
            <button
              type="button"
              onClick={handleDeferStripe}
              className="w-full rounded-full border border-[#2e3138] bg-[#18191d] py-3 text-sm font-bold text-white hover:bg-[#24262c] transition cursor-pointer"
            >
              Not Now
            </button>
          </div>
        </div>
      )}

      <StripeOnboardingPromptModal
        isOpen={showCreatorStripePrompt}
        onClose={() => setShowCreatorStripePrompt(false)}
      />

      {requestPaymentKey != null && canGenerateLink && requestPaymentOptions.length > 0 && (
        <RequestPaymentModal
          currency={offer.currency}
          payerName={partyName}
          options={requestPaymentOptions}
          remainingBalance={remainingBalance}
          initialKey={requestPaymentKey === 'default' ? undefined : requestPaymentKey}
          isSubmitting={payingKey != null}
          isDirectOffer={isStandalone}
          onClose={() => setRequestPaymentKey(null)}
          onSubmit={(option) => void handleSubmitRequestPayment(option)}
        />
      )}

      {/* TEMP-DISABLED: external payment modal hidden — chat only for now.
      {showSettlementModal && canRecordSettlement && (
        <OffPlatformSettlementModal
          currency={offer.currency}
          remainingBalance={remainingBalance}
          presets={settlementPresets}
          isSubmitting={proposeSettlement.isPending}
          onClose={() => setShowSettlementModal(false)}
          onSubmit={(input) => void handleRecordSettlement(input)}
        />
      )}
      */}
    </div>
  );
};
